import { Engine } from './core/Engine';
import { InputManager } from './core/InputManager';
import { AssetLoader } from './core/AssetLoader';
import { audioManager } from './core/AudioManager';
import { HighScoreManager } from './core/HighScoreManager';
import { TextInputHandler } from './core/TextInputHandler';
import { WebGLRenderer } from './renderer/WebGLRenderer';
import { Maze, LevelData } from './world/Maze';
import { StateMachine } from './states/StateMachine';
import { MenuState } from './states/MenuState';
import { PlayState } from './states/PlayState';
import { GameOverState } from './states/GameOverState';
import { HighScoreEntryState } from './states/HighScoreEntryState';
import { HighScoresState } from './states/HighScoresState';
import { TextRenderer } from './ui/TextRenderer';
import { HUD } from './ui/HUD';
import level1Data from './world/levels/level1.json';

export class Game {
  private engine: Engine;
  private input: InputManager;
  private assets: AssetLoader;
  private renderer!: WebGLRenderer;
  private stateMachine: StateMachine;
  private maze!: Maze;
  private textRenderer!: TextRenderer;
  private hud!: HUD;
  private highScoreManager!: HighScoreManager;
  private textInput!: TextInputHandler;
  private playState!: PlayState;
  private gameOverState!: GameOverState;
  private highScoreEntryState!: HighScoreEntryState;
  private highScoresState!: HighScoresState;

  constructor(canvas: HTMLCanvasElement) {
    this.engine = new Engine();
    this.input = new InputManager();
    this.assets = new AssetLoader();
    this.renderer = new WebGLRenderer(canvas);
    this.stateMachine = new StateMachine();
  }

  async init(): Promise<void> {
    await this.assets.loadAll({
      images: [
        { name: 'maze', url: '/sprites/maze.png' },
        { name: 'text', url: '/sprites/text.png' },
      ],
      shaders: [
        { name: 'sprite.vert', url: '/shaders/sprite.vert' },
        { name: 'sprite.frag', url: '/shaders/sprite.frag' },
      ],
    });

    await this.renderer.init(this.assets);

    this.maze = new Maze(level1Data as LevelData);

    this.textRenderer = new TextRenderer(this.renderer.getBatch(), this.renderer.getTextAtlas());

    this.hud = new HUD(this.textRenderer, this.renderer.getBatch(), this.renderer.getSpriteAtlas());

    // Initialize shared managers
    this.highScoreManager = new HighScoreManager();
    this.textInput = new TextInputHandler();

    // Initialize audio (will be resumed on first user interaction)
    audioManager.init();

    // Load initial high scores
    this.highScoreManager.loadScores();

    this.setupStates();

    this.engine.onUpdate(this.update.bind(this));
    this.engine.onRender(this.render.bind(this));
  }

  private setupStates(): void {
    const menuState = new MenuState(
      this.renderer,
      this.input,
      this.textRenderer,
      this.highScoreManager,
      () => this.stateMachine.change('play'),
      () => this.stateMachine.change('highScores')
    );

    this.playState = new PlayState(
      this.renderer,
      this.input,
      this.maze,
      this.hud,
      this.highScoreManager,
      () => this.handleGameOver(),
      () => this.stateMachine.change('menu')
    );

    this.gameOverState = new GameOverState(this.renderer, this.input, this.textRenderer, () =>
      this.stateMachine.change('play')
    );

    this.highScoreEntryState = new HighScoreEntryState(
      this.renderer,
      this.textRenderer,
      this.textInput,
      this.highScoreManager,
      () => {
        // After submitting, show high scores
        this.highScoresState.setAutoReturn(true, 8000);
        this.stateMachine.change('highScores');
      },
      () => {
        // If cancelled, just go to game over
        this.stateMachine.change('gameOver');
      }
    );

    this.highScoresState = new HighScoresState(
      this.renderer,
      this.input,
      this.textRenderer,
      this.highScoreManager,
      () => {
        this.highScoreManager.clearSubmittedScoreId();
        this.stateMachine.change('menu');
      }
    );

    this.stateMachine.add('menu', menuState);
    this.stateMachine.add('play', this.playState);
    this.stateMachine.add('gameOver', this.gameOverState);
    this.stateMachine.add('highScoreEntry', this.highScoreEntryState);
    this.stateMachine.add('highScores', this.highScoresState);

    this.stateMachine.change('menu');
  }

  private handleGameOver(): void {
    const score = this.playState.getScore();
    const level = this.playState.getLevel();

    // Check if it's a high score
    if (this.highScoreManager.isHighScore(score)) {
      // Show name entry screen
      this.highScoreEntryState.setScoreData(score, level);
      this.stateMachine.change('highScoreEntry');
    } else {
      // Just show game over
      this.gameOverState.setScore(score);
      this.stateMachine.change('gameOver');
    }
  }

  private update(dt: number): void {
    this.stateMachine.update(dt);
  }

  private render(): void {
    this.stateMachine.render();
  }

  start(): void {
    this.engine.start();
  }
}
